import React, { useEffect, useState, useCallback, useMemo } from "react";
import { environment } from "environments/environment";
import { SelectCustom } from "../../../Select";
import { useCreditSelector } from "../../../../stores/product/product.store";
import { CreditState } from "../../../../stores/credit/credit-state.interface";
import { useCreditDispatch } from "../../../../stores/credit/credit.store";
import { setShipping } from "../../../../stores/credit/credit.reducer";
import { fetchTotalThunk } from "../../../../stores/credit/thunks/fetch-total.thunk";

interface SboxProps {}

type SelectOption = { label: string; value: string };

/** Folosit pentru _ONP_ (optionId 6). >30kg → doar hub-uri, fără city/sector. */
export const Sbox: React.FC<SboxProps> = () => {
    const translateText = useCreditSelector((state: CreditState) => state.text);
    const productId = useCreditSelector((state: CreditState) => state.productId);
    const method = useCreditSelector((state: CreditState) => state.shipping.selectShippingMethod);
    const hubOnly = Boolean(method?.hubOnly);

    const [zones, setZones] = useState<SelectOption[]>([]);
    const [cities, setCities] = useState<SelectOption[]>([]);
    const [pickups, setPickups] = useState<SelectOption[]>([]);
    const [selectedZone, setSelectedZone] = useState<SelectOption | null>(null);
    const [selectedCity, setSelectedCity] = useState<SelectOption | null>(null);
    const [selectedPickup, setSelectedPickup] = useState<SelectOption | null>(null);
    const dispatch = useCreditDispatch();

    const hubMeta = useMemo(() => {
        const map: Record<string, { zoneId: string; cityId: string }> = {};
        (method?.pickups || []).forEach((pickup) => {
            map[String(pickup.code)] = {
                zoneId: pickup.zoneId ? String(pickup.zoneId) : '',
                cityId: pickup.cityId ? String(pickup.cityId) : '',
            };
        });
        return map;
    }, [method?.pickups]);

    useEffect(() => {
        setSelectedZone(null);
        setSelectedCity(null);
        setSelectedPickup(null);
        setZones([]);
        setPickups([]);
        setCities([]);

        if (hubOnly) {
            setPickups((method?.pickups || []).map((pickup) => ({
                label: pickup.name,
                value: String(pickup.code),
            })));
            return;
        }

        fetch(environment.urls.credits.getCities, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ optionId: 6, productId })
        })
            .then(response => response.json())
            .then(data => {
                const formattedCities = data.map((city: any) => ({
                    label: city.name,
                    value: city.code
                }));
                setCities(formattedCities);
            })
            .catch(error => console.error('Error fetching cities:', error));
    }, [productId, hubOnly, method?.pickups]);

    const onChangeCity = useCallback((option: SelectOption | null) => {
        setSelectedCity(option);
        setSelectedZone(null);
        setSelectedPickup(null);

        setZones([]);
        setPickups([]);

        if (!option) return;

        fetch(environment.urls.credits.getRegions, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ cityId: option.value, optionId: 6, productId })
        })
            .then(response => response.json())
            .then(data => {
                const formattedZones = data.map((zone: any) => ({
                    label: zone.name,
                    value: zone.code
                }));
                setZones(formattedZones);
            })
            .catch(error => console.error('Error fetching cities:', error));
    }, [productId]);

    const onChangeZone = (option: SelectOption | null) => {
        setSelectedZone(option);
        setSelectedPickup(null);
        setPickups([]);

        if (!option) return;

        fetch(environment.urls.credits.getPickups, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ zoneId: option.value, productId })
        })
            .then(response => response.json())
            .then(data => {
                const formattedPickups = data.map((pickup: any) => ({
                    label: pickup.name,
                    value: pickup.code
                }));
                setPickups(formattedPickups);
            })
            .catch(error => console.error('Error fetching pickups:', error));
    };

    const onChangePickup = (option: SelectOption | null) => {
        setSelectedPickup(option);
        if (!option) return;

        const meta = hubMeta[option.value];
        dispatch(setShipping({
            zone: hubOnly ? (meta?.zoneId || option.value) : selectedZone!.value,
            cityId: hubOnly ? (meta?.cityId || '') : selectedCity!.value,
            pickup: option.value,
        }));
        dispatch(fetchTotalThunk());
    };

    if (hubOnly) {
        return (
            <div>
                {pickups.length > 0 && (
                    <div className="w-full mb-6">
                        <label htmlFor="pickup"
                               className="text-sm font-medium mb-3 block text-black">{translateText.pickup}<span
                            className="text-red-500"> *</span></label>
                        <SelectCustom options={pickups} onChange={onChangePickup} value={selectedPickup}/>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div>
            <div className="w-full mb-6">
                <label htmlFor="region" className="text-sm font-medium mb-3 block text-black">{translateText.region}<span
                    className="text-red-500"> *</span></label>
                <SelectCustom options={cities} onChange={onChangeCity} value={selectedCity}/>
            </div>
            {zones.length > 0 && (
                <div className="w-full mb-6">
                    <label htmlFor="city"
                           className="text-sm font-medium mb-3 block text-black">{translateText.sector}<span
                               className="text-red-500"> *</span></label>
                    <SelectCustom options={zones} onChange={onChangeZone} value={selectedZone}/>
                </div>
            )}
            {pickups.length > 0 && (
                <div className="w-full mb-6">
                    <label htmlFor="pickup"
                           className="text-sm font-medium mb-3 block text-black">{translateText.pickup}<span
                               className="text-red-500"> *</span></label>
                    <SelectCustom options={pickups} onChange={onChangePickup} value={selectedPickup}/>
                </div>
            )}
        </div>
    );
};
